import React from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import Button from 'core-system/Button'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import { CurrencyType } from 'features/Programs/Shared/types'
import { AutoTopupSubscription } from 'redux/auto-topup/autoTopupTypes'
import { AppState } from 'redux/config/store'
import { formatCurrency } from 'shared/CurrencyFunctions'
import PaymentProcessingMessage from './PaymentProcessingMessage'
import NonDirectCheckoutHelper, {
  formatVendorName,
  isNonDirectCheckoutVendor,
} from '../shared/components/NonDirectCheckoutHelper'

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 40rem;
  padding: 2rem;
  border: 1px solid ${(props) => props.theme.palette.primary.pink100};
  border-radius: 1rem;
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.palette.primary.pink50} 0%,
    ${(props) => props.theme.palette.secondary.purple50} 100%
  );
`

const DetailStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    flex-direction: column;
    gap: 0.125rem;
  }
`

interface CheckoutConfirmationSummaryProps {
  subscription: AutoTopupSubscription
  isPaymentProcessing: boolean
  paymentProcessingTimedOut: boolean
}

const CheckoutConfirmationSummary = React.memo(
  (props: CheckoutConfirmationSummaryProps) => {
    const { subscription } = props
    const { isPaymentProcessing, paymentProcessingTimedOut } = props
    const bike = subscription.bike
    const { profileData } = useSelector(
      (state: AppState) => state.employeePlatform
    )
    const conversionRates = useSelector(
      (state: AppState) => state.application.properties
    )
    const currency: CurrencyType =
      (profileData?.worksiteCurrency as CurrencyType) || 'USD'
    const vendorName = formatVendorName(bike?.vendor)
    const showNonDirectCheckoutHelper = Boolean(
      bike && isNonDirectCheckoutVendor(bike.vendor)
    )
    const handleContinue = () => {
      if (bike?.productUrl) {
        window.open(bike.productUrl, '_blank', 'noopener,noreferrer')
      }
    }

    return (
      <Card>
        <Text variant='h2'>You're all set up!</Text>
        <Text variant='h4' textColor={palette.secondary.purple800}>
          Next, finish your order with {vendorName}.
        </Text>
        <Text variant='body1' textColor={palette.text.secondary}>
          {isPaymentProcessing
            ? 'Your Fleet Card is being loaded with the appropriate funds.'
            : 'Your Fleet Card is loaded with the appropriate funds.'}
        </Text>
        {isPaymentProcessing ? (
          <PaymentProcessingMessage timedOut={paymentProcessingTimedOut} />
        ) : null}
        {showNonDirectCheckoutHelper && bike ? (
          <NonDirectCheckoutHelper bike={bike} />
        ) : null}
        <DetailStack>
          {bike?.name ? (
            <DetailRow>
              <Text variant='action3' textColor={palette.text.secondary}>
                Bike
              </Text>
              <Text variant='action2'>{bike.name}</Text>
            </DetailRow>
          ) : null}
          <DetailRow>
            <Text variant='action3' textColor={palette.text.secondary}>
              Monthly recurring payment
            </Text>
            <Text variant='action2'>
              {formatCurrency(currency, subscription.amount, conversionRates)}
            </Text>
          </DetailRow>
        </DetailStack>
        <Button
          disabled={!bike?.productUrl || isPaymentProcessing}
          onClick={handleContinue}
          size='large'
        >
          Continue To {vendorName}
        </Button>
      </Card>
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  CheckoutConfirmationSummary.displayName = 'CheckoutConfirmationSummary'
}

export default CheckoutConfirmationSummary
